import { useState, useEffect, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useSearchParams, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Send, Sparkles } from "lucide-react";
import { YouTubePlayer } from "./YouTubePlayer";
import GeneralInfoStep from "./form-steps/GeneralInfoStep";
import TreatmentsStep from "./form-steps/TreatmentsStep";
import ClienteleStep from "./form-steps/ClienteleStep";
import CommunicationStep from "./form-steps/CommunicationStep";
import OffersStep from "./form-steps/OffersStep";
import { formSchema } from "./form-steps/formSchema";
import logo from "@/assets/logo_salone_vincente.png";

// Storage keys for persistence
const STORAGE_KEY = 'beautyForm_draft';
const STORAGE_KEY_FAILED = 'beautyForm_failedSubmission';
const STORAGE_KEY_ERRORS = 'beautyForm_errorLogs';

const STEPS = [
  {
    id: 1,
    title: "Informazioni Generali",
    icon: "✨",
  },
  {
    id: 2,
    title: "Trattamenti",
    icon: "💆",
  },
  {
    id: 3,
    title: "Clientela",
    icon: "👥",
  },
  {
    id: 4,
    title: "Comunicazione",
    icon: "📢",
  },
  {
    id: 5,
    title: "Offerte",
    icon: "🎁",
  },
];

interface WebhookError {
  type: 'timeout' | 'http' | 'network' | 'unknown';
  message: string;
  details?: string;
  statusCode?: number;
  attemptNumber: number;
}

// Helper function to log errors persistently
const logError = (error: WebhookError, payload: any) => {
  try {
    const errorLog = {
      timestamp: Date.now(),
      error,
      payload,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    const logs = JSON.parse(localStorage.getItem(STORAGE_KEY_ERRORS) || '[]');
    logs.push(errorLog);
    // Keep only last 10 errors
    localStorage.setItem(STORAGE_KEY_ERRORS, JSON.stringify(logs.slice(-10)));

    // Send to Sentry with full context
    Sentry.captureException(new Error(`Webhook ${error.type} error: ${error.message}`), {
      level: 'error',
      tags: {
        errorType: error.type,
        attemptNumber: error.attemptNumber,
        statusCode: error.statusCode || 'N/A',
      },
      contexts: {
        webhook: {
          url: 'https://n8n.srv1035791.hstgr.cloud/webhook/f9286435-9ee3-495e-b7a2-b76a72607cc3',
          payload: payload,
          error: error,
        },
      },
    });
  } catch (e) {
    console.error('Failed to log error:', e);
  }
};

const BeautyForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // Track navigation to prevent race conditions with toast portal
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track debounce timeout for auto-save to prevent blocking main thread
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track AbortController for canceling webhook requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Set Sentry user context based on URL parameters
  useEffect(() => {
    const groupId = searchParams.get("groupId");
    const id = searchParams.get("Id");

    if (groupId || id) {
      Sentry.setUser({
        id: id || undefined,
        username: groupId || undefined,
      });

      Sentry.setTags({
        groupId: groupId || 'none',
        userId: id || 'none',
      });
    }
  }, [searchParams]);

  // Cleanup effect - track component mount/unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      // Clear any pending navigation timeouts
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
    };
  }, []);

  const handleVideoEnd = () => {
    if (isMountedRef.current) {
      setShowForm(true);
    }
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onTouched",
    defaultValues: {
      centerName: "",
      ownerName: "",
      phoneNumber: "",
      email: "",
      address: "",
      city: "",
      province: "",
      zipCode: "",
      website: "",
      landmarks: "",
      mainTreatment: "",
      treatmentReason: "",
      differentiators: "",
      shortTermResults: "",
      longTermResults: "",
      recommendations: "",
      sessionDuration: "",
      openingHours: "",
      clientRadius: "",
      appearInAds: "",
      voiceInAds: "",
      competitors: "",
      currentOffer: "",
      reasonForCollaboration: "",
      expectations: "",
      doubts: "",
    },
  });

  // Debounced auto-save function to prevent blocking main thread on every keystroke
  const debouncedAutoSave = useCallback((value: any) => {
    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout - save after 500ms of no changes
    autoSaveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          data: value,
          step: currentStep,
          timestamp: Date.now()
        }));
        console.log(`[${new Date().toISOString()}] 💾 Form auto-saved (debounced)`);
      } catch (e) {
        console.error('Failed to save form data:', e);
      }
    }, 500); // 500ms debounce
  }, [currentStep]);

  // Effect 1: Auto-save form data on changes (debounced to improve performance)
  useEffect(() => {
    const subscription = form.watch((value) => {
      debouncedAutoSave(value);
    });

    return () => {
      subscription.unsubscribe();
      // Clear pending auto-save on unmount
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [form, debouncedAutoSave]);

  // Effect 2: Restore form data on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const { data, step, timestamp } = JSON.parse(saved);
        const ageInHours = (Date.now() - timestamp) / (1000 * 60 * 60);

        // Only restore if less than 24 hours old
        if (ageInHours < 24) {
          const hasData = Object.values(data).some((v: any) => v && v !== "");

          if (hasData && isMountedRef.current) {
            // Show resume prompt
            setShowResumePrompt(true);

            // Auto-restore data
            form.reset(data);
            setCurrentStep(step);
            setShowForm(true);

            toast.info("Dati precedenti ripristinati", {
              description: "Abbiamo recuperato i tuoi dati salvati.",
              duration: 5000,
            });
          }
        } else {
          // Clear old data
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        console.error('Failed to restore form data:', e);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []); // Only run once on mount

  // Effect 3: beforeunload protection
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const values = form.getValues();
      const hasData = Object.values(values).some(v => v && v !== "");

      if (hasData && !isSubmitting) {
        e.preventDefault();
        e.returnValue = "Hai dati non salvati. Sei sicuro di voler uscire?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isSubmitting, form]);

  // Cancel submission function
  const handleCancelSubmission = () => {
    console.log(`[${new Date().toISOString()}] ❌ Cancellazione invio richiesta dall'utente`);

    // Abort ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Reset states
    if (isMountedRef.current) {
      setIsSubmitting(false);
      setRetryAttempt(0);
      toast.info("Invio annullato", {
        description: "Puoi riprovare quando vuoi. I tuoi dati sono salvati.",
      });
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (isSubmitting) return;

    // Set submitting state immediately to prevent double submission
    setIsSubmitting(true);
    setRetryAttempt(0); // Reset retry counter

    const startTime = Date.now();
    console.log(`[${new Date().toISOString()}] 🚀 Inizio invio modulo`);

    try {
      const groupId = searchParams.get("groupId") || "";
      const id = searchParams.get("Id") || "";

      // Generate unique submission ID
      const submissionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      console.log(`[${new Date().toISOString()}] 📝 Submission ID: ${submissionId}`);

      // Check if already submitted recently (prevent duplicates)
      const lastSubmission = localStorage.getItem("lastSubmission");
      if (lastSubmission) {
        const { timestamp, id: lastId } = JSON.parse(lastSubmission);
        const timeSinceLastSubmit = Date.now() - timestamp;
        if (timeSinceLastSubmit < 5000) {
          // 5 seconds debounce
          console.log(`[${new Date().toISOString()}] ⚠️ Submission troppo recente, ignorata`);
          toast.warning("Attendere prima di inviare nuovamente");
          if (isMountedRef.current) {
            setIsSubmitting(false);
          }
          return;
        }
      }

      const payload = {
        submission_id: submissionId,
        groupId: groupId,
        Id: id,
        nome_centro: values.centerName,
        nome_cognome_titolare: values.ownerName,
        telefono: values.phoneNumber,
        email: values.email,
        indirizzo: values.address,
        citta: values.city,
        provincia: values.province,
        cap: values.zipCode,
        sito_web: values.website || null,
        punti_riferimento: values.landmarks || null,
        trattamento_principale: values.mainTreatment,
        motivo_trattamento: values.treatmentReason,
        differenziatori: values.differentiators,
        risultati_breve_termine: values.shortTermResults,
        risultati_lungo_termine: values.longTermResults || null,
        raccomandazioni: values.recommendations || null,
        durata_seduta: values.sessionDuration || null,
        orari_apertura: values.openingHours,
        raggio_clientela: values.clientRadius,
        apparire_in_ads: values.appearInAds,
        voce_in_ads: values.voiceInAds || null,
        concorrenti: values.competitors || null,
        offerta_attuale: values.currentOffer || null,
        motivo_collaborazione: values.reasonForCollaboration,
        aspettative: values.expectations,
        dubbi: values.doubts || null,
      };

      console.log(`[${new Date().toISOString()}] 📦 Payload preparato:`, payload);

      // Webhook sending function with timeout
      const sendWebhook = async (attemptNumber: number): Promise<{ success: boolean; error?: WebhookError }> => {
        console.log(`[${new Date().toISOString()}] 🔄 Tentativo ${attemptNumber}/3`);

        // Update retry attempt state for UI
        if (isMountedRef.current) {
          setRetryAttempt(attemptNumber);
        }

        const controller = new AbortController();
        abortControllerRef.current = controller; // Store for cancellation
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

        try {
          const response = await fetch(
            "https://n8n.srv1035791.hstgr.cloud/webhook/f9286435-9ee3-495e-b7a2-b76a72607cc3",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json; charset=utf-8",
              },
              body: JSON.stringify(payload),
              signal: controller.signal,
            },
          );

          // Controlla lo status code
          if (!response.ok) {
            const errorDetail: WebhookError = {
              type: 'http',
              message: `Errore HTTP ${response.status}`,
              details: response.statusText,
              statusCode: response.status,
              attemptNumber
            };

            console.error(`[${new Date().toISOString()}] ❌ HTTP Error ${response.status} su tentativo ${attemptNumber}`);
            return { success: false, error: errorDetail };
          }

          console.log(`[${new Date().toISOString()}] ✅ Webhook inviato con successo (tentativo ${attemptNumber})`);
          return { success: true };

        } catch (error) {

          let errorDetail: WebhookError;

          // Timeout
          if (error instanceof Error && error.name === "AbortError") {
            errorDetail = {
              type: 'timeout',
              message: 'Il server non ha risposto entro 10 secondi',
              details: 'Il webhook n8n potrebbe essere sovraccarico o offline',
              attemptNumber
            };
            console.error(`[${new Date().toISOString()}] ⏱️ Timeout su tentativo ${attemptNumber}`);
          }
          // Errori di rete (CORS, DNS, connessione)
          else if (error instanceof TypeError) {
            errorDetail = {
              type: 'network',
              message: 'Errore di connessione al server',
              details: error.message.includes('Failed to fetch') 
                ? 'Impossibile raggiungere il server. Verifica la connessione internet o contatta il supporto.'
                : error.message,
              attemptNumber
            };
            console.error(`[${new Date().toISOString()}] 🌐 Network Error su tentativo ${attemptNumber}:`, error.message);
          }
          // Altri errori
          else {
            errorDetail = {
              type: 'unknown',
              message: 'Errore sconosciuto',
              details: error instanceof Error ? error.message : 'Errore non identificato',
              attemptNumber
            };
            console.error(`[${new Date().toISOString()}] ❌ Unknown Error su tentativo ${attemptNumber}:`, error);
          }

          return { success: false, error: errorDetail };
        } finally {
          // Always clear timeout to prevent memory leaks
          clearTimeout(timeoutId);
        }
      };

      // Retry mechanism - 3 attempts with exponential backoff
      let webhookSent = false;
      let lastError: WebhookError | undefined;
      const retryDelays = [1000, 2000, 4000]; // Exponential backoff: 1s, 2s, 4s

      for (let attempt = 1; attempt <= 3 && !webhookSent; attempt++) {
        const result = await sendWebhook(attempt);
        webhookSent = result.success;

        if (!result.success) {
          lastError = result.error;

          if (attempt < 3) {
            const delay = retryDelays[attempt - 1];
            console.log(`[${new Date().toISOString()}] ⏳ Attendo ${delay}ms prima del prossimo tentativo...`);
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }

      if (!webhookSent && lastError) {
        throw lastError;
      }

      // Reset retry state on success
      if (isMountedRef.current) {
        setRetryAttempt(0);
      }

      // Save submission to localStorage
      localStorage.setItem(
        "lastSubmission",
        JSON.stringify({
          id: submissionId,
          timestamp: Date.now(),
          groupId,
          Id: id,
        }),
      );

      // Clear saved form data on successful submission
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY_FAILED);

      const totalTime = Date.now() - startTime;
      console.log(`[${new Date().toISOString()}] ✨ Invio completato in ${totalTime}ms`);

      // Prepare navigation parameters
      const params = new URLSearchParams();
      if (groupId) params.set("groupId", groupId);
      if (id) params.set("Id", id);
      const targetPath = `/3${params.toString() ? `?${params.toString()}` : ""}`;

      // Show success toast
      toast.success("Modulo inviato con successo!", {
        description: "Reindirizzamento al prossimo video...",
        duration: 2500,
      });

      // Reset submitting state immediately to prevent frozen UI
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }

      // Navigate after toast display period with small buffer
      // Store timeout in ref for proper cleanup on unmount
      navigationTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          console.log(`[${new Date().toISOString()}] ✅ Navigating to ${targetPath}`);
          navigate(targetPath);
        } else {
          console.log(`[${new Date().toISOString()}] ⚠️ Navigation cancelled - component unmounted`);
        }
      }, 2600); // Navigate after toast duration (2500ms) + small buffer
    } catch (error) {
      console.error(`[${new Date().toISOString()}] 💥 ERRORE CRITICO:`, error);

      // Save form data on failure for recovery
      try {
        localStorage.setItem(STORAGE_KEY_FAILED, JSON.stringify({
          data: values,
          error: error,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.error('Failed to save form data on error:', e);
      }

      // Log error persistently for debugging
      if (error && typeof error === 'object' && 'type' in error) {
        logError(error as WebhookError, values);
      }

      let errorTitle = "Errore nell'invio del modulo";
      let errorDescription = "I tuoi dati sono stati salvati. Riprova tra qualche secondo.";

      // Se è un WebhookError, mostra dettagli specifici
      if (error && typeof error === 'object' && 'type' in error) {
        const webhookError = error as WebhookError;
        
        switch (webhookError.type) {
          case 'timeout':
            errorTitle = "⏱️ Timeout del Server";
            errorDescription = `Il server non ha risposto entro 10 secondi (dopo ${webhookError.attemptNumber} tentativi). ${webhookError.details || ''}`;
            break;
            
          case 'http':
            errorTitle = `❌ Errore Server (${webhookError.statusCode})`;
            errorDescription = webhookError.statusCode === 404
              ? "Il webhook non è stato trovato. Contatta il supporto tecnico."
              : webhookError.statusCode && webhookError.statusCode >= 500
              ? `Il server sta avendo problemi (${webhookError.statusCode}). Riprova tra qualche minuto.`
              : `${webhookError.message}. ${webhookError.details || ''}`;
            break;
            
          case 'network':
            errorTitle = "🌐 Errore di Connessione";
            errorDescription = `${webhookError.message}. ${webhookError.details || 'Verifica la tua connessione internet.'}`;
            break;
            
          case 'unknown':
          default:
            errorTitle = "❌ Errore Imprevisto";
            errorDescription = `${webhookError.details || 'Errore sconosciuto'}. Se il problema persiste, contatta il supporto.`;
            break;
        }
      }
      
      toast.error(errorTitle, {
        description: errorDescription,
        duration: 8000, // 8 secondi per dare tempo di leggere
      });

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setIsSubmitting(false);
        setRetryAttempt(0); // Reset retry counter on error
      }
    }
  };
  const nextStep = async () => {
    if (!isMountedRef.current) return;

    setIsValidating(true);
    try {
      const fields = getFieldsForStep(currentStep);
      const isValid = await form.trigger(fields);

      if (isValid && currentStep < STEPS.length) {
        if (isMountedRef.current) {
          setCurrentStep(currentStep + 1);
        }
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else if (!isValid) {
        // Find first error field and scroll to it
        const firstErrorField = fields.find((field) => form.formState.errors[field]);
        if (firstErrorField) {
          const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
          errorElement?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    } finally {
      if (isMountedRef.current) {
        setIsValidating(false);
      }
    }
  };
  const prevStep = () => {
    if (!isMountedRef.current) return;

    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };
  const getFieldsForStep = (step: number): (keyof z.infer<typeof formSchema>)[] => {
    switch (step) {
      case 1:
        return ["centerName", "ownerName", "phoneNumber", "email", "address"];
      case 2:
        return ["mainTreatment", "treatmentReason", "differentiators", "shortTermResults"];
      case 3:
        return ["openingHours", "clientRadius"];
      case 4:
        return ["appearInAds", "competitors"];
      case 5:
        return ["reasonForCollaboration", "expectations"];
      default:
        return [];
    }
  };
  const progress = (currentStep / STEPS.length) * 100;
  return (
    <div className="min-h-screen gradient-subtle py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Video Section - Only show on first step and before form */}
        {currentStep === 1 && !showForm && (
          <div className="animate-fade-in mb-12">
            <div className="text-center mb-8">
              <img src={logo} alt="Salone Vincente" className="w-40 h-40 object-contain mx-auto mb-6 animate-float" />
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
                QUESTIONARIO CENTRO ESTETICO
              </h1>
            </div>

            <div className="card p-4 shadow-elegant">
              <YouTubePlayer videoId="IuFDNA-tsZE" onVideoEnd={handleVideoEnd} />
            </div>

            {!showForm && (
              <div className="text-center mt-8">
                <p className="text-xl font-semibold text-foreground">
                  Arriva alla fine del video per compilare il questionario
                </p>
              </div>
            )}
          </div>
        )}

        {/* Logo for steps after first */}
        {currentStep > 1 && showForm && (
          <div className="text-center mb-8 animate-fade-in">
            <img src={logo} alt="Salone Vincente" className="w-32 h-32 object-contain mx-auto mb-4" />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
              Questionario Centro Estetico
            </h1>
          </div>
        )}

        {/* Form Section */}
        {showForm && (
          <>
            <div className="text-center mb-8 animate-fade-in">
              <p className="text-muted-foreground text-lg">
                Compila il modulo per aiutarci a conoscere meglio il tuo centro
              </p>
            </div>

            {/* Progress Bar */}
            <div className="mb-8 card p-6 shadow-elegant">
              <div className="flex justify-between mb-4">
                {STEPS.map((step) => (
                  <div
                    key={step.id}
                    className={`flex flex-col items-center gap-2 ${step.id === currentStep ? "text-primary font-semibold" : step.id < currentStep ? "text-secondary" : "text-muted-foreground"}`}
                  >
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl transition-all duration-300 ${step.id === currentStep ? "bg-gradient-to-br from-primary to-secondary shadow-elegant scale-110" : step.id < currentStep ? "bg-secondary/20" : "bg-muted"}`}
                    >
                      {step.icon}
                    </div>
                    <span className="text-xs md:text-sm hidden md:block">{step.title}</span>
                  </div>
                ))}
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Form */}
            <div className="card p-8 shadow-soft animate-slide-up">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {currentStep === 1 && <GeneralInfoStep form={form} />}
                  {currentStep === 2 && <TreatmentsStep form={form} />}
                  {currentStep === 3 && <ClienteleStep form={form} />}
                  {currentStep === 4 && <CommunicationStep form={form} />}
                  {currentStep === 5 && <OffersStep form={form} />}

                  {/* Navigation Buttons */}
                  <div className="flex gap-4 pt-6 border-t">
                    {currentStep > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={prevStep}
                        disabled={isSubmitting || isValidating}
                        className="flex-1"
                      >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Indietro
                      </Button>
                    )}

                    {currentStep < STEPS.length ? (
                      <Button
                        type="button"
                        onClick={nextStep}
                        disabled={isValidating || isSubmitting}
                        className="flex-1 bg-gradient-to-r from-primary to-secondary hover:opacity-90 transition-opacity"
                      >
                        {isValidating ? "Validazione..." : "Avanti"}
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    ) : (
                      <div className="flex-1 flex gap-2">
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex-1 bg-gradient-to-r from-primary to-secondary hover:opacity-90 transition-opacity"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {isSubmitting
                            ? retryAttempt > 0
                              ? `Tentativo ${retryAttempt}/3...`
                              : "Invio in corso..."
                            : "Invia Modulo"
                          }
                        </Button>
                        {isSubmitting && retryAttempt > 0 && (
                          <Button
                            type="button"
                            variant="destructive"
                            onClick={handleCancelSubmission}
                            className="whitespace-nowrap"
                          >
                            Annulla
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </form>
              </Form>
            </div>

            {/* Footer */}
            <div className="text-center mt-8 text-sm text-muted-foreground">
              <p>I tuoi dati sono al sicuro e verranno trattati con la massima riservatezza</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Wrap component with Sentry ErrorBoundary for automatic error tracking
export default Sentry.withErrorBoundary(BeautyForm, {
  fallback: ({ error, resetError }) => (
    <div className="min-h-screen gradient-subtle py-12 px-4 flex items-center justify-center">
      <div className="max-w-2xl w-full card p-8 shadow-elegant text-center">
        <div className="mb-6">
          <img src={logo} alt="Salone Vincente" className="w-32 h-32 object-contain mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Si è verificato un errore
          </h1>
          <p className="text-muted-foreground mb-4">
            Ci scusiamo per l'inconveniente. I tuoi dati sono stati salvati.
          </p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-red-800 font-mono">
              {error instanceof Error ? error.message : 'Errore sconosciuto'}
            </p>
        </div>
        <div className="flex gap-4 justify-center">
          <Button onClick={resetError} className="bg-gradient-to-r from-primary to-secondary">
            Riprova
          </Button>
          <Button variant="outline" onClick={() => window.location.href = "/"}>
            Torna alla Home
          </Button>
        </div>
      </div>
    </div>
  ),
  showDialog: false, // Don't show Sentry's default dialog
});
